import { formatMD, formatMDHM } from '@indipia/shared'
import {
  OrderApiError, ORDER_ERROR_NETWORK, ORDER_ERROR_UNKNOWN, toOrderApiError,
  type OrderPatch, type OrderPatchItem, type OrderScheduleAffected, type OrderSettingItem, type OrderStatusCode,
  type OrderVendorRow, type ScheduleTaskOrderItem, type VendorCandidate,
} from '../api/orderApi'
import type { ConfirmProgressStep } from './partnerStatus'

// C-3 자재파트너배정·발주정보발송 / 자재파트너 카드 / H-5 공용 — 문구·라벨·판정·변환 순수 함수 (WP5 5단계 U0-2).
// 화면 파일에 한글 문구를 흩뿌리지 않도록 문구는 ORDER_MESSAGE 한 곳에 모은다. 서버 message 원문·내부 코드는 화면에 쓰지 않는다.
// 알림톡·인앱 알림 문구는 서버 몫이라 만들지 않는다. 값이 없으면 기본값·0·가정값으로 채우지 않는다.

// ── 상수·라벨 ─────────────────────────────────────────────────────────

/** 품목·글자 수 제한(서버 검증과 같은 기준) */
export const ORDER_LIMITS = {
  itemsMax: 30,
  itemNameMax: 100,
  itemSpecMax: 100,
  itemUnitMax: 25,
  addressMax: 300,
  noteMax: 300,
  /** 소수 자리 수 */
  qtyDecimals: 2,
} as const

/** 품목 단위 선택지 7종(서버는 자유 문자열 ≤25 유지) */
export const ORDER_UNITS = ['개', '본', '롤', '박스', '장', '포', '식'] as const

/** 공통코드 ZEU_ORDER_STATUS 와 글자 일치(FE에서 공통코드 조회 경로가 없어 상수) */
export const ORDER_STATUS_LABEL: Record<OrderStatusCode, string> = {
  DRAFT: '발송 전',
  REPLY_WAIT: '회신 대기',
  REREPLY_WAIT: '재회신 대기',
  RESEND_NEEDED: '다시 보내기 필요',
  CONFIRMED: '발주 확정',
  DECLINED: '재배정 필요',
  SHIP_WAIT: '배송정보 대기',
  SHIP_REPLIED: '배송정보 회신',
  RECEIVED: '입고 완료',
}

/** 불가 사유 라벨(4단계 DECLINE_REASONS 와 글자 일치) */
export const DECLINE_REASON_LABEL: Record<string, string> = {
  NO_STOCK: '재고 부족',
  NO_DEADLINE: '납기 불가',
  PRICE_CHANGE: '단가 변동',
  ETC: '기타',
}

/** 입고 수량 확인 라벨 */
export const QTY_CHECK_LABEL: Record<string, string> = {
  NORMAL: '이상 없음',
  ISSUE: '부족·파손',
}

/** 세부 내역 팝업 라벨 — 공통코드(ZEU_ORDER_SEND_METHOD·ZEU_ORDER_RECEIPT_METHOD·ZEU_ORDER_CLOSED_REASON·ZEU_SEND_STATUS) 이름과 글자 일치 */
export const SEND_METHOD_LABEL: Record<string, string> = { MANUAL: '직접 발송', AUTO: '자동 발송', OFFLINE: '대행 입력' }
export const RECEIPT_METHOD_LABEL: Record<string, string> = {
  FOREMAN_LINK: '반장 입고 링크', MANAGER: '담당자 직접 확인', WORK_REPORT: '작업보고 입고 사진',
}
export const CLOSED_REASON_LABEL: Record<string, string> = {
  RESENT: '다시 보내서 마감',
  VENDOR_CHANGED: '거래처 변경으로 마감',
  TASK_DELETED: '공종·일정 삭제로 마감',
  FOREMAN_CHANGED: '반장 변경으로 마감',
  CONTENT_CHANGED: '발주 내용 변경으로 마감',
}
export const SEND_STATUS_LABEL: Record<string, string> = {
  QUEUED: '발송중', SENT: '발송됨', FAILED: '실패(재시도중)', DEAD: '최종실패', SKIPPED: '제외',
}

/** 자동 지정 사유 4종(문구 초안, 근거: 계획서 W5-05) */
export const ASSIGN_REASON_TEXT: Record<string, string> = {
  SAME_SITE: '같은 현장·같은 공종에서 거래한 곳이에요',
  MAIN_VENDOR: '발주 설정의 주거래처예요',
  RECENT_VENDOR: '이 공종에서 가장 최근에 거래한 곳이에요',
  RECOMMENDED: '거래 횟수가 가장 많아 추천 1순위로 골랐어요',
}

export const ASSIGN_METHOD = { AUTO: 'AUTO', MANUAL: 'MANUAL' } as const

export const ORDER_MESSAGE = {
  // 팝업·목록
  popupTitle: '자재파트너배정 · 발주정보발송',
  newVendor: '신규 등록',
  notDirect: '발주 없음',
  notDirectCard: '자재 발주가 필요 없는 공종이에요 (시공파트너가 자재 포함 시공)',
  datesMissing: '발주일·입고일 미정',
  deliveryConfirmedBadge: '입고 확정',
  unassigned: '미배정',
  assignAuto: '자동배정',
  assignAutoInfo: '자동배정 ⓘ',
  assignManual: '직접배정',
  tagAuto: '자동매칭',
  tagManual: '직접지정',
  evidenceOpen: '자동배정 근거 보기',
  signalError: '신호 정보를 불러오지 못했어요',
  signalUnknown: '표시할 수 없는 신호 상태예요',
  noDirectRows: '발주할 공종이 없어요 · 발주 설정에서 발주를 켜 주세요',
  goOrderSettings: '발주 설정으로',
  taskNotFound: '이 공종을 찾을 수 없어요',
  loadFailed: '발주 정보를 불러오지 못했어요',
  retry: '다시 시도',
  vendorPickerLabel: '자재파트너 선택',
  vendorNone: '지정 안 함',
  /** 아직 고르지 않았을 때 콤보 입력칸 안내 — 목록의 '지정 안 함' 항목과는 별개 */
  vendorPlaceholder: '자재파트너를 골라 주세요',
  vendorEmpty: '이 공종을 거래하는 자재파트너가 없어요',
  vendorRegister: '자재파트너 등록',
  vendorCurrent: '현재',
  vendorMain: '주거래처',
  vendorDeclinedBefore: '1차 불가',
  noTradeHistory: '아직 거래 이력이 없어요',
  historyLink: '이력·평가보기 ›',
  // 상태·안내
  resendNeeded: '입고일이 바뀌었어요 · 거래처에 다시 보내 주세요',
  newVendorSend: '새 거래처에 발주정보를 보내 주세요',
  readOnlyConfirmed: '이미 거래처가 확정한 발주예요',
  readOnlyShipWait: '배송정보를 기다리는 중이라 고칠 수 없어요',
  readOnlyShipReplied: '배송정보가 회신된 뒤에는 바꿀 수 없어요',
  readOnlyUnknown: '지금 상태에서는 고칠 수 없어요',
  vendorLockedReason: '배송정보가 회신된 뒤에는 바꿀 수 없어요',
  changeVendorTitle: '거래처를 바꿀까요?',
  changeVendorBody: '발송한 링크가 닫히고 새 거래처에 다시 보내야 해요. 이전 거래처에는 따로 알림이 가지 않아요. 바꿀까요?',
  changeVendorOk: '바꾸기',
  cancel: '취소',
  // 처리 결과 토스트
  assignedToast: (vendorName: string) => `자재파트너를 지정했어요 · ${vendorName}`,
  replacedToast: (vendorName: string) => `자재파트너를 바꿨어요 · ${vendorName}`,
  releasedToast: '자재파트너 지정을 풀었어요',
  datesSavedToast: '날짜를 저장했어요',
  // 발주정보 작성
  itemsTitle: '품목',
  itemName: '품목명',
  itemSpec: '규격',
  itemQty: '수량',
  itemUnit: '단위',
  addItem: '품목 추가',
  addItemMax: '품목은 30줄까지 넣을 수 있어요',
  required: '필수',
  deliveryDate: '입고희망일',
  orderDate: '발주일',
  clearDate: '지우기',
  editDates: '날짜 수정',
  editDone: '완료',
  lift: '양중',
  liftYes: '요청',
  liftNo: '요청 없음',
  address: '현장 주소',
  note: '남길 말',
  saving: '저장 중...',
  saved: '저장됐어요',
  saveFailed: '저장하지 못했어요',
  saveRetry: '다시 저장',
  saveCheckInput: '입력을 확인해 주세요',
  panelCheckInput: '입력한 내용을 확인해 주세요',
  // 발송
  sendFirst: '발주정보 보내기',
  /** 결과만 직접등록(SIF-3) 거래처 — [발주정보 보내기] 자리. 누르면 같은 발주정보발송 팝업을 연다 */
  recordOrder: '발주 기록',
  sendAgain: '다시 보내기',
  sentToast: (vendorName: string) => `${vendorName}에 발주정보를 보냈어요`,
  recordedToast: (vendorName: string) => `${vendorName} 발주를 기록했어요`,
  readyToRecord: '기록할 준비가 됐어요',
  batchSentToast: (ok: number, failed: number) => failed > 0 ? `${ok}곳 보냈어요 · ${failed}곳은 못 보냈어요` : `${ok}곳에 발주정보를 보냈어요`,
  batchSummary: (ok: number, failed: number) => `${ok}곳 보냈어요 · ${failed}곳은 못 보냈어요`,
  sendResultUnknown: '결과를 확인하지 못했어요 · 목록을 새로 불러왔으니 상태를 확인하고 다시 보내 주세요',
  partialDateOnly: (md: string) => `입고일은 ${md}로 바꿨어요 · 발송은 못 했어요 · 다시 보내기를 눌러 주세요`,
  availDateResend: (md: string) => `${md} 입고로 다시 보내기`,
  pickOtherVendor: '다른 거래처 고르기',
  sendNeedItems: '품목을 입력해 주세요',
  sendNeedOrderDate: '발주일을 정해 주세요',
  /** 결과만 직접등록(SIF-3 C-3) — 입고희망일 대신 거래처 시스템에서 확인한 입고예정일 */
  deliveryDateDirect: '입고예정일',
  sendNeedDeliveryDirect: '입고예정일을 정해 주세요',
  /** 결과만 직접등록 [발주 기록] 버튼 옆 안내 */
  directRequiredHint: '발주일 · 입고예정일만 필수',
  optional: '(선택)',
  /** 결과만 직접등록 — '남길 말' 칸(ORDER_NOTE)을 거래처에 보내지 않는 메모로 쓴다 */
  memo: '메모',
  sendNeedDelivery: '입고희망일을 정해 주세요',
  sendNeedAddress: '현장 주소를 입력해 주세요',
  sendNeedFix: '입력한 내용을 확인해 주세요',
  sendNotAllowed: '지금 상태에서는 보낼 수 없어요',
  writeState: { EMPTY: '미작성', WRITING: '작성중', READY: '작성완료' },
  readyToSend: '보낼 준비가 됐어요',
  nextTask: '작성 완료·다음 공종',
  unitPlaceholder: '단위',
  batchButton: (n: number) => `체크한 ${n}곳 발주정보 알림톡 발송`,
  batchButtonEmpty: '발송할 곳을 체크해 주세요',
  batchWritingWarn: '체크한 곳은 작성완료만 보내요. 작성 중인 곳은 오른쪽에서 마무리해 주세요',
  batchConfirmTitle: (n: number) => `${n}곳에 발주정보를 보낼까요?`,
  batchSend: '보내기',
  batchCannotSend: '보낼 수 없음',
  // 회신
  replyTitle: '거래처 답장',
  replyAccepted: '가능',
  replyDeclined: '불가',
  liftOk: '양중 가능',
  liftNg: '양중 불가',
  // 알림톡 미리보기
  previewTitle: '알림톡 미리보기',
  previewFailed: '미리보기를 불러오지 못했어요',
  // 충돌
  conflictTitle: '다른 곳에서 먼저 수정했어요',
  conflictBody: '내가 입력한 내용과 최신 내용이 달라요.',
  conflictTakeLatest: '최신 내용으로 바꾸기',
  conflictKeepMine: '내 입력으로 저장',
  // 근거 모달
  evidenceTitle: '자동배정 근거',
  evidenceSub: '자재파트너 자동배정 근거',
  evidenceFailed: '자동배정 근거를 불러오지 못했어요',
  evidenceReasonUnknown: '배정 근거를 표시할 수 없어요',
  evidenceNotDirect: '발주하지 않는 공종이에요',
  evidenceRecalc: (name: string) => `지금 기준으로 다시 계산하면 ${name}이 1순위예요`,
  evidenceReasonHead: '배정 사유',
  evidenceRecalcHead: '지금 기준 재계산',
  evidenceCandidatesHead: '후보 목록',
  // 날짜 경고(C9)
  warnAlreadySent: '이미 발주정보를 보낸 공종이에요',
  warnOrderAfterDelivery: '발주일이 입고희망일보다 늦어요',
  warnDeliveryAfterStart: "입고희망일이 착공일 이후예요 · 입고가 늦으면 '입고 지연'으로 표시돼요",
  warnStaleDelivery: '거래처가 알려준 배송정보와 입고일이 달라졌어요',
  warnDeliveryReceived: '이미 입고된 공종이에요',
  affectedResend: "입고일이 바뀌어 '다시 보내기 필요'가 됐어요 · 거래처에 다시 보내 주세요",
  affectedShipClosed: '배송정보 요청 링크가 닫혔어요',
  // 진행 요약
  stepTitles: ['발주정보 요청', '발주정보 회신', '배송정보 요청', '배송정보 회신', '입고 내역'] as const,
  stepNotSent: '아직 보내지 않았어요',
  /** 결과만 직접등록(SIF-3) 거래처의 1단계 문구 — 알림톡을 보내지 않고 거래처 시스템에 발주한 뒤 기록한다 */
  stepRecordInVendorSystem: '거래처 시스템에 발주 후 기록',
  stepPickVendor: '거래처를 먼저 골라 주세요',
  stepChangeRequest: '변경 요청',
  stepWaitingReply: '회신을 기다리는 중',
  stepShipBeforeDay: '입고 전날에 요청해요',
  stepNone: '-',
  noItemsYet: '아직 입력한 품목이 없어요',
  // 검증(서버 검증과 같은 기준)
  vItemNameRequired: '품목명을 입력해 주세요',
  vItemNameMax: '품목명은 100자까지 쓸 수 있어요',
  vItemSpecMax: '규격은 100자까지 쓸 수 있어요',
  vItemQtyRequired: '수량을 입력해 주세요',
  vItemQtyInvalid: '0보다 큰 숫자를 입력해 주세요 (소수 둘째 자리까지)',
  vItemUnitRequired: '단위를 입력해 주세요',
  vItemUnitMax: '단위는 25자까지 쓸 수 있어요',
  vItemsMax: '품목은 30줄까지 넣을 수 있어요',
  vAddressMax: '현장 주소는 300자까지 쓸 수 있어요',
  vNoteMax: '남길 말은 300자까지 쓸 수 있어요',
  vLiftInvalid: '양중 요청 여부를 선택해 주세요',
  // H-5
  h5LeadMissing: '발주 리드타임 기준이 없어요 · 공종 기준정보를 확인해 주세요',
  h5SkippedLead: (n: number) => `리드타임 기준이 없는 공종 ${n}개는 저장하지 않아요`,
  // 오류(§5.2)
  error: {
    ORDER_NOT_DIRECT: '발주하지 않는 공종이에요 · 발주 설정에서 발주를 켜 주세요',
    ORDER_VENDOR_INVALID: '이 거래처는 고를 수 없어요 · 거래중지이거나 등록된 거래처가 아니에요',
    ORDER_VENDOR_EXISTS: '이미 지정된 거래처예요',
    ORDER_VENDOR_LIMIT: '한 공종에는 거래처를 최대 5곳까지 지정할 수 있어요',
    ORDER_VENDOR_LOCKED: '배송정보가 회신된 뒤에는 거래처를 바꾸거나 내용을 고칠 수 없어요',
    ORDER_VENDOR_NOT_FOUND: '발주 정보를 찾을 수 없어요 · 새로 불러올게요',
    TASK_NOT_FOUND: '발주 정보를 찾을 수 없어요 · 새로 불러올게요',
    ORDER_RELEASE_NOT_ALLOWED: '이미 보낸 기록이 있어 지정을 풀 수 없어요 · 거래처를 바꿔 주세요',
    ORDER_ITEMS_REQUIRED: '품목을 1줄 이상 입력해 주세요',
    ORDER_DELIVERY_DATE_REQUIRED: '입고희망일을 정해 주세요',
    VALIDATION_ERROR: '입력한 내용을 확인해 주세요',
    ORDER_SEND_NOT_ALLOWED_NO_CHANGE: '바뀐 내용이 없어서 다시 보낼 수 없어요 · 입고희망일이나 품목을 바꾼 뒤 보내 주세요',
    ORDER_SEND_NOT_ALLOWED: '지금 상태에서는 보낼 수 없어요',
    ORDER_VENDOR_CONTACT_MISSING: '거래처 연락처가 없어 보내지 못했어요 · 자재파트너 정보에서 연락처를 확인해 주세요',
    ORDER_SEND_FAILED: '발송에 실패했어요 · 잠시 후 다시 시도해 주세요',
    CONFLICT: '다른 곳에서 처리 중이에요 · 잠시 후 다시 시도해 주세요',
    ORDER_CONTENT_CONFLICT: '다른 곳에서 먼저 수정했어요',
    ORDER_SITE_STAFF_MISSING: '현장 담당자가 지정되어 있지 않아요 · 현장 담당자를 먼저 지정해 주세요',
    INVALID_LEAD_DAYS: '발주 리드타임은 1일 이상이어야 해요',
    FORBIDDEN: '이 현장의 자재 발주를 다룰 권한이 없어요',
    NETWORK: '연결이 원활하지 않아요 · 네트워크를 확인하고 다시 시도해 주세요',
    SERVER: '잠시 후 다시 시도해 주세요',
    SAVE_FAILED: '저장하지 못해 보내지 않았어요 · 저장 상태를 확인해 주세요',
    NO_AVAIL_DATE: '가능한 날짜가 없어 다시 보낼 수 없어요',
    UNKNOWN: '처리에 실패했어요 · 다시 시도해 주세요',
  },

  // C-3 팝업 고유 조각(공종별 목록·발주정보 작성 패널·일괄 확인 창)
  popup: {
    listTitle: '공종별 배정',
    pickPrompt: '왼쪽에서 공종을 누르면 여기서 발주정보를 작성하고 바로 보낼 수 있어요',
    autoSaveHint: '변경하면 바로 저장돼요',
    listHint: '공종 · 거래처를 누르면 오른쪽에서 발주정보를 작성해요',
    unassignedTitle: '자재파트너를 먼저 배정하세요',
    unassignedHint: '왼쪽 목록에서 자재파트너를 고르면 바로 작성할 수 있어요',
    headStart: (md: string) => `착공 ${md}`,
    headOrder: (md: string) => `발주일 ${md}`,
    pickNotDirect: '발주하지 않는 공종이에요 · 자재 발주가 필요 없어요',
    loadingLabel: '자재 발주 정보를 불러오는 중',
    periodRange: (a: string, b: string) => `${a} ~ ${b}`,
    batchCheck: (name: string) => `${name} 일괄 발송 대상으로 선택`,
    batchDeliveryAt: (md: string) => `입고 ${md}`,
    batchSendable: '보낼 수 있는 곳',
    itemRemove: (n: number) => `${n}번째 품목 삭제`,
    itemLabel: (n: number, label: string) => `${n}. ${label}`,
    noteCount: (n: number, max: number) => `${n}/${max}`,
    addressHint: '거래처가 알림톡에서 보게 되는 배송지예요',
    contactLabel: '연락처',
    panelHeading: '발주정보',
    signalErrorBadge: '신호 오류',
    sendInFlight: '보내는 중',
    close: '닫기',
    signalBadge: (colorName: string) => `발주 신호 ${colorName}`,
  },

  // 상세창 자재파트너 카드
  card: {
    title: '자재파트너',
    tagAuto: '자동매칭',
    tagAutoLabel: '자동매칭 근거 보기',
    tagManual: '직접지정',
    tagNone: '미배정',
    notDirect: '자재 발주가 필요 없는 공종이에요 (시공파트너가 자재 포함 시공)',
    loadError: '자재파트너 정보를 불러오지 못했어요',
    signalMissing: '신호 정보를 불러오지 못했어요',
    datesUnset: '발주일·입고일 미정',
    datesEdit: '발주일·입고일 수정',
    confirmed: '입고 확정',
    history: '이력·평가보기 ›',
    noItems: '아직 입력한 품목이 없어요',
    orderSummary: '발주 내용',
    resendNeeded: '입고일이 바뀌었어요 · 거래처에 다시 보내 주세요',
    liftYes: '양중 요청',
    liftNo: '양중 요청 없음',
    pickOther: '다른 거래처 고르기',
    noVendor: '배정된 자재파트너가 없어요',
    addVendor: '자재파트너 추가',
    addVendorCancel: '취소',
    vendorMax: '자재파트너는 한 공종에 최대 5곳까지 지정할 수 있어요',
    vendorCount: (n: number) => `${n}곳`,
  },

  // H-5 발주 설정(하단 저장줄·후보 지표·이탈 보호·오류)
  settings: {
    // SIF-1 발주·시공 방식 선택(별도발주 / 발주없음 / 자재파트너가 시공)
    modeColHead: '발주·시공 방식',
    modeSeparate: '별도발주',
    modeNone: '발주없음',
    modeVendorConstruct: '자재파트너가 시공',
    modeSelectLabel: (name: string) => `${name} 발주·시공 방식`,
    modeNoneInfo: '시공파트너가 자재까지 가져와 시공해요 · 발주 알림 없음',
    modeVendorInfo: '이 자재파트너가 시공까지 맡아요 · 시공파트너 섭외 없음',
    // H-5 표 머리글·방식 옵션 한 줄 설명(SIF-1 1-1)
    modeTableHead: '발주 · 시공 방식',
    // 방식 변경은 소급 적용하지 않음(확정) — 상단 안내 3번째 줄
    modeChangeNotice: '방식 변경은 새로 만드는 스케줄부터 적용돼요. 이미 만든 현장은 간트 공종 상세창의 [발주 · 시공 방식]에서 바꿔 주세요.',
    modeSeparateDesc: '자재는 자재파트너에 발주 · 시공은 시공파트너',
    modeNoneDesc: '시공파트너가 자재까지 가져와 시공',
    modeVendorConstructDesc: '자재파트너가 자재 + 시공 · 시공파트너 섭외 없음',
    // SIF-1 4-3 현장별 방식 변경(상세창 '이 현장만') — 회사 기본값(H-5)과 별개로 이 현장·이 공종만 바꾼다
    siteOverrideHint: '이 현장에만 적용돼요',
    siteOverrideSaved: '방식을 바꿨어요',
    // 방식을 바꿨는데 발주정보가 이미 거래처에 나간 경우 / 종료일이 지난 공종
    orderInfoResend: '새 조건으로 다시 보내 주세요 · 이미 보낸 발주정보는 이전 조건 그대로예요',
    modePastLocked: '이미 지난 공종은 방식을 바꿀 수 없어요',
    noChange: '바꾼 내용이 없어요',
    changed: (n: number) => `바꾼 공종 ${n}개`,
    saving: '저장 중...',
    saved: '저장됐어요',
    skipped: (n: number) => `리드타임 기준이 없는 공종 ${n}개는 저장하지 않아요`,
    leadMissing: '발주 리드타임 기준이 없어요 · 공종 기준정보를 확인해 주세요',
    noHistory: '아직 거래 이력이 없어요',
    dirtySubject: '바꾼 공종 설정',
    loadFail: '저장된 설정을 불러오지 못했습니다.',
    invalidLeadDays: '발주 리드타임은 1일 이상이어야 해요',
    forbidden: '이 현장의 자재 발주를 다룰 권한이 없어요',
    network: '연결이 원활하지 않아요 · 네트워크를 확인하고 다시 시도해 주세요',
    server: '잠시 후 다시 시도해 주세요',
    unknown: '처리에 실패했어요 · 다시 시도해 주세요',
  },
} as const

// ── 오류 문구 ─────────────────────────────────────────────────────────

const ERROR_BY_CODE: Record<string, string> = ORDER_MESSAGE.error

/** 서버 오류의 재조회가 필요한 코드(발주 정보를 찾을 수 없음) */
export function isOrderNotFoundCode(code: string): boolean {
  return code === 'ORDER_VENDOR_NOT_FOUND' || code === 'TASK_NOT_FOUND'
}

/**
 * 오류 → 화면 문구(§5.2). 서버 message 원문·requestId 는 쓰지 않는다. 없는 코드는 UNKNOWN 문구.
 * ctx.vendorName 이 있으면 `{거래처} · {문구}` 로 표시한다(일괄 발송 실패 행 등).
 */
export function orderErrorMessage(err: unknown, ctx?: { vendorName?: string }): string {
  const e = err instanceof OrderApiError ? err : toOrderApiError(err)
  const msg = baseErrorMessage(e)
  return ctx?.vendorName ? `${ctx.vendorName} · ${msg}` : msg
}

function baseErrorMessage(e: OrderApiError): string {
  if (e.code === 'ORDER_SEND_NOT_ALLOWED') {
    return e.reason === 'NO_CONTENT_CHANGE' ? ORDER_MESSAGE.error.ORDER_SEND_NOT_ALLOWED_NO_CHANGE : ORDER_MESSAGE.error.ORDER_SEND_NOT_ALLOWED
  }
  if (Object.prototype.hasOwnProperty.call(ERROR_BY_CODE, e.code) && !SPECIAL_KEYS.has(e.code)) return ERROR_BY_CODE[e.code]
  if (e.status === 403) return ORDER_MESSAGE.error.FORBIDDEN
  if (e.code === ORDER_ERROR_NETWORK) return ORDER_MESSAGE.error.NETWORK
  if (e.status != null && e.status >= 500) return ORDER_MESSAGE.error.SERVER
  return ORDER_MESSAGE.error.UNKNOWN
}

/** 코드로 직접 조회하면 안 되는 내부 키(합성 키·상황 키) */
const SPECIAL_KEYS = new Set<string>([
  'ORDER_SEND_NOT_ALLOWED_NO_CHANGE', 'ORDER_SEND_NOT_ALLOWED', 'FORBIDDEN', 'NETWORK', 'SERVER', 'UNKNOWN',
])

export type ServerFieldTarget =
  | { kind: 'item'; index: number; key: 'itemName' | 'itemSpec' | 'itemQty' | 'itemUnit' }
  | { kind: 'field'; name: 'deliveryAddress' | 'orderNote' | 'liftReqYn' | 'deliveryDate' }

/** 서버 VALIDATION_ERROR `field` → 화면 칸. 모르는 값·없음은 null(패널 상단 문구) */
export function mapServerField(field: string | null | undefined): ServerFieldTarget | null {
  if (!field) return null
  const m = /^items\[(\d+)\]\.(itemName|itemSpec|itemQty|itemUnit)$/.exec(field)
  if (m) return { kind: 'item', index: Number(m[1]), key: m[2] as 'itemName' | 'itemSpec' | 'itemQty' | 'itemUnit' }
  if (field === 'deliveryAddress' || field === 'orderNote' || field === 'liftReqYn' || field === 'deliveryDate') return { kind: 'field', name: field }
  return null
}

// ── 직접발주·공종 행 모델 ─────────────────────────────────────────────

/** 직접발주 = 발주 설정에 orderYn === 'N' 행이 없음('N이 아니면 Y'). 행 없음·null·Y 는 직접발주(서버 DirectOrderResolver 와 같은 기준) */
export function isDirectOrder(settings: OrderSettingItem[], workTypeCode: string): boolean {
  return !settings.some(s => s.workTypeCode === workTypeCode && s.orderYn === 'N')
}

/**
 * 공종(task) 단위 직접발주 판정 — 상세창 '이 현장만' 방식(orderModeOverride)이 있으면 그 값 우선, 없으면 회사 설정(H-5).
 * 'NONE'만 발주 비대상이고 'SEPARATE'·'VENDOR_CONSTRUCT'(자재파트너가 시공도 발주는 함)는 대상 — 서버 DirectOrderResolver.isDirect(task)와 같은 규칙.
 */
export function isDirectOrderTask(settings: OrderSettingItem[], task: Pick<ScheduleTaskOrderItem, 'workTypeCode' | 'orderModeOverride'>): boolean {
  if (task.orderModeOverride) return task.orderModeOverride !== 'NONE'
  return isDirectOrder(settings, task.workTypeCode)
}

export interface MaterialTaskRowModel {
  taskId: number
  workTypeCode: string
  workTypeName: string
  startDate: string
  endDate: string
  directOrder: boolean
  /** Q1(스케줄 공종 순서) 원본 */
  task: ScheduleTaskOrderItem
  /** 이 공종의 발주 거래처 행(활성만, 대부분 0~1줄). 직접발주 아님이면 항상 빈 배열 */
  vendorRows: OrderVendorRow[]
  orderDate: string | null
  deliveryDate: string | null
  deliveryConfirmed: boolean
}

/**
 * 공종 행 모델 — Q1 순서 유지, 각 공종에 A1 행(없으면 빈 배열)·직접발주 여부 연결.
 * A1에만 있고 Q1에 없는 행은 버리고(소프트삭제 경합), 응답의 중복 orderVendorId는 마지막 값만 쓴다.
 * 교체되어 닫힌 행(REPLACED)은 활성 행이 아니므로 제외한다.
 */
export function buildTaskRows(
  tasks: ScheduleTaskOrderItem[], rows: OrderVendorRow[], settings: OrderSettingItem[],
): MaterialTaskRowModel[] {
  const byId = new Map<number, OrderVendorRow>()
  for (const r of rows) byId.set(r.orderVendorId, r)
  const byTask = new Map<number, OrderVendorRow[]>()
  for (const r of byId.values()) {
    if ((r.orderStatusCode as string) === 'REPLACED') continue
    const list = byTask.get(r.taskId)
    if (list) list.push(r); else byTask.set(r.taskId, [r])
  }
  return tasks.map(t => {
    const direct = isDirectOrderTask(settings, t)
    return {
      taskId: t.taskId,
      workTypeCode: t.workTypeCode,
      workTypeName: t.workTypeName,
      startDate: t.taskStartDate,
      endDate: t.taskEndDate,
      directOrder: direct,
      task: t,
      vendorRows: direct ? (byTask.get(t.taskId) ?? []) : [],
      orderDate: t.orderDate,
      deliveryDate: t.deliveryDate,
      deliveryConfirmed: t.deliveryConfirmedYn === 'Y',
    }
  })
}

// ── 상태별 화면 규칙 ──────────────────────────────────────────────────

const EDITABLE_STATUS: ReadonlySet<string> = new Set(['DRAFT', 'REPLY_WAIT', 'REREPLY_WAIT', 'RESEND_NEEDED', 'DECLINED'])
const VENDOR_CHANGE_CONFIRM_STATUS: ReadonlySet<string> = new Set(['REPLY_WAIT', 'REREPLY_WAIT', 'RESEND_NEEDED', 'CONFIRMED', 'SHIP_WAIT'])
const VENDOR_LOCKED_STATUS: ReadonlySet<string> = new Set(['SHIP_REPLIED', 'RECEIVED'])

/** 발주정보 작성 패널 편집 가능 상태(§3.4) */
export function isOrderEditable(status: string): boolean { return EDITABLE_STATUS.has(status) }
/** 거래처 변경 전 확인 창이 필요한 상태(DRAFT·DECLINED 는 확인 없음) */
export function needsVendorChangeConfirm(status: string): boolean { return VENDOR_CHANGE_CONFIRM_STATUS.has(status) }
/** 거래처 변경 불가 상태(서버 ORDER_VENDOR_LOCKED) */
export function isVendorLocked(status: string): boolean { return VENDOR_LOCKED_STATUS.has(status) }
/** 지정 해제는 발송 기록이 없는 DRAFT 에서만(서버 A5) — 목록·카드가 같은 기준을 씀 */
export function canRelease(row: OrderVendorRow): boolean { return row.orderStatusCode === 'DRAFT' && row.lastSend == null }

/** 읽기 전용 상태의 이유 한 줄. 편집 가능하면 null */
export function readOnlyReason(status: string): string | null {
  if (isOrderEditable(status)) return null
  switch (status) {
    case 'CONFIRMED': return ORDER_MESSAGE.readOnlyConfirmed
    case 'SHIP_WAIT': return ORDER_MESSAGE.readOnlyShipWait
    case 'SHIP_REPLIED':
    case 'RECEIVED': return ORDER_MESSAGE.readOnlyShipReplied
    default: return ORDER_MESSAGE.readOnlyUnknown
  }
}

// ── 발송 가능 판정 ────────────────────────────────────────────────────

export interface SendAction {
  /** 버튼 라벨. 보낼 수 없는 상태면 null */
  label: string | null
  /** 한 곳씩 발송 가능 */
  single: boolean
  /** 일괄 체크 가능(처음 보내기·내용 변경 재발송만, 최적안 M1) */
  batch: boolean
  /** DECLINED 의 가능한 날짜(없으면 null) — 있으면 '{M/D} 입고로 다시 보내기' 추가 */
  availDate: string | null
}

const NO_SEND: SendAction = { label: null, single: false, batch: false, availDate: null }

/** §5.4 상태별 발송 버튼·일괄 체크 가능 여부 */
/**
 * 발송 버튼. direct=결과만 직접등록(SIF-3) 거래처 — 알림톡을 보내지 않고 [발주 기록](담당자 대행 입력 A10, 가능 회신)만 한다.
 * 일괄 발송 대상이 아니다.
 */
export function getSendAction(row: OrderVendorRow | null, direct = false): SendAction {
  if (!row) return NO_SEND
  if (direct) {
    switch (row.orderStatusCode) {
      case 'DRAFT':
      case 'RESEND_NEEDED':
      case 'REPLY_WAIT':
      case 'REREPLY_WAIT':
      case 'DECLINED': return { label: ORDER_MESSAGE.recordOrder, single: true, batch: false, availDate: null }
      default: return NO_SEND
    }
  }
  switch (row.orderStatusCode) {
    case 'DRAFT': return { label: ORDER_MESSAGE.sendFirst, single: true, batch: true, availDate: null }
    case 'RESEND_NEEDED': return { label: ORDER_MESSAGE.sendAgain, single: true, batch: true, availDate: null }
    case 'REPLY_WAIT':
    case 'REREPLY_WAIT': return { label: ORDER_MESSAGE.sendAgain, single: true, batch: false, availDate: null }
    case 'DECLINED': return { label: ORDER_MESSAGE.sendAgain, single: true, batch: false, availDate: row.lastReply?.availDate ?? null }
    default: return NO_SEND
  }
}

// ── 초안·검증·패치 ────────────────────────────────────────────────────

export interface OrderDraftItem { itemName: string; itemSpec: string; itemQty: string; itemUnit: string }

/** 발주정보 입력 초안(입력칸 그대로의 문자열). liftReqYn 은 서버 값이 Y/N 밖이면 ''(선택 없음 — Y/N 으로 가정하지 않음) */
export interface OrderDraft {
  items: OrderDraftItem[]
  liftReqYn: 'Y' | 'N' | ''
  deliveryAddress: string
  orderNote: string
}

export interface DraftItemErrors { itemName?: string; itemSpec?: string; itemQty?: string; itemUnit?: string }
export interface DraftErrors {
  /** 초안 줄 인덱스 → 칸별 오류 */
  items: Record<number, DraftItemErrors>
  itemsTotal?: string
  liftReqYn?: string
  deliveryAddress?: string
  orderNote?: string
}

export function emptyDraftItem(): OrderDraftItem { return { itemName: '', itemSpec: '', itemQty: '', itemUnit: '' } }

/** 서버 행 → 초안. 품목이 없으면 빈 줄 1개로 시작 */
export function draftFromRow(row: OrderVendorRow): OrderDraft {
  const items = row.items.length > 0
    ? row.items.map(i => ({ itemName: i.itemName, itemSpec: i.itemSpec ?? '', itemQty: String(i.itemQty), itemUnit: i.itemUnit }))
    : [emptyDraftItem()]
  return {
    items,
    liftReqYn: row.liftReqYn === 'Y' ? 'Y' : row.liftReqYn === 'N' ? 'N' : '',
    deliveryAddress: row.deliveryAddress ?? '',
    orderNote: row.orderNote ?? '',
  }
}

function isBlankItem(i: OrderDraftItem): boolean {
  return !i.itemName.trim() && !i.itemSpec.trim() && !i.itemQty.trim() && !i.itemUnit.trim()
}

const QTY_RE = /^\d+(\.\d{1,2})?$/

/** 수량 문자열이 0보다 큰 숫자(소수 2자리까지)인지 */
export function isValidQty(s: string): boolean {
  const t = s.trim()
  return QTY_RE.test(t) && Number(t) > 0
}

/** 서버 검증과 같은 기준의 초안 검증. 완전히 빈 줄은 무시(오류 아님). direct=결과만 직접등록 — 양중·현장 주소 칸이 없어 검사하지 않는다 */
export function validateDraft(draft: OrderDraft, direct = false): DraftErrors {
  const out: DraftErrors = { items: {} }
  let filled = 0
  draft.items.forEach((it, idx) => {
    if (isBlankItem(it)) return
    filled += 1
    const e: DraftItemErrors = {}
    const name = it.itemName.trim()
    const spec = it.itemSpec.trim()
    const unit = it.itemUnit.trim()
    if (!name) e.itemName = ORDER_MESSAGE.vItemNameRequired
    else if (name.length > ORDER_LIMITS.itemNameMax) e.itemName = ORDER_MESSAGE.vItemNameMax
    if (spec.length > ORDER_LIMITS.itemSpecMax) e.itemSpec = ORDER_MESSAGE.vItemSpecMax
    if (!it.itemQty.trim()) e.itemQty = ORDER_MESSAGE.vItemQtyRequired
    else if (!isValidQty(it.itemQty)) e.itemQty = ORDER_MESSAGE.vItemQtyInvalid
    if (!unit) e.itemUnit = ORDER_MESSAGE.vItemUnitRequired
    else if (unit.length > ORDER_LIMITS.itemUnitMax) e.itemUnit = ORDER_MESSAGE.vItemUnitMax
    if (Object.keys(e).length > 0) out.items[idx] = e
  })
  if (filled > ORDER_LIMITS.itemsMax) out.itemsTotal = ORDER_MESSAGE.vItemsMax
  if (!direct) {
    if (draft.liftReqYn !== 'Y' && draft.liftReqYn !== 'N') out.liftReqYn = ORDER_MESSAGE.vLiftInvalid
    if (draft.deliveryAddress.trim().length > ORDER_LIMITS.addressMax) out.deliveryAddress = ORDER_MESSAGE.vAddressMax
  }
  if (draft.orderNote.trim().length > ORDER_LIMITS.noteMax) out.orderNote = ORDER_MESSAGE.vNoteMax
  return out
}

export function hasDraftErrors(e: DraftErrors): boolean {
  return Object.keys(e.items).length > 0 || !!e.itemsTotal || !!e.liftReqYn || !!e.deliveryAddress || !!e.orderNote
}

function draftItemsToPatchItems(draft: OrderDraft): OrderPatchItem[] {
  return draft.items.filter(i => !isBlankItem(i)).map(i => ({
    itemName: i.itemName.trim(),
    itemSpec: i.itemSpec.trim() ? i.itemSpec.trim() : null,
    itemQty: Number(i.itemQty.trim()),
    itemUnit: i.itemUnit.trim(),
  }))
}

function sameItems(a: OrderPatchItem[], b: OrderVendorRow['items']): boolean {
  if (a.length !== b.length) return false
  return a.every((x, i) => {
    const y = b[i]
    return x.itemName === y.itemName.trim()
      && x.itemSpec === (y.itemSpec && y.itemSpec.trim() ? y.itemSpec.trim() : null)
      && x.itemQty === Number(y.itemQty)
      && x.itemUnit === y.itemUnit.trim()
  })
}

/**
 * 서버 행과 초안을 비교해 달라진 필드만 담은 A4 패치. 변경이 없으면 null.
 * 오류가 있는 필드는 제외한다(items 는 전체 목록이 유효할 때만 전송). expectedVer = row.contentVer.
 * 공백 정규화(앞뒤 공백 제거·빈 문자열=null)는 서버와 같은 기준.
 */
export function toOrderPatch(row: OrderVendorRow, draft: OrderDraft): OrderPatch | null {
  const errs = validateDraft(draft)
  const patch: OrderPatch = { expectedVer: row.contentVer }
  let changed = false

  const itemsValid = Object.keys(errs.items).length === 0 && !errs.itemsTotal
  if (itemsValid) {
    const next = draftItemsToPatchItems(draft)
    if (!sameItems(next, row.items)) { patch.items = next; changed = true }
  }
  if (!errs.liftReqYn && draft.liftReqYn !== '' && draft.liftReqYn !== row.liftReqYn) { patch.liftReqYn = draft.liftReqYn; changed = true }
  if (!errs.deliveryAddress) {
    const next = draft.deliveryAddress.trim() ? draft.deliveryAddress.trim() : null
    const cur = row.deliveryAddress && row.deliveryAddress.trim() ? row.deliveryAddress.trim() : null
    if (next !== cur) { patch.deliveryAddress = next; changed = true }
  }
  if (!errs.orderNote) {
    const next = draft.orderNote.trim() ? draft.orderNote.trim() : null
    const cur = row.orderNote && row.orderNote.trim() ? row.orderNote.trim() : null
    if (next !== cur) { patch.orderNote = next; changed = true }
  }
  return changed ? patch : null
}

function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * 결과만 직접등록(SIF-3 C-3) [발주 기록] 날짜 기본값 — 발주일 = 오늘, 입고예정일 = 착공 하루 전(착공일을 못 읽으면 null).
 * 저장된 값이 없을 때 입력칸에 보여 주고, [발주 기록] 때 함께 저장한다.
 */
export function directDefaultDates(startDate: string, now: Date = new Date()): { orderDate: string; deliveryDate: string | null } {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(startDate)
  const dayBefore = m ? ymdLocal(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) - 1)) : null
  return { orderDate: ymdLocal(now), deliveryDate: dayBefore }
}

/** 저장값이 없는 날짜를 결과만 직접등록 기본값으로 채운 행(작성완료·[발주 기록] 점검용) */
export function withDirectDefaultDates(row: OrderVendorRow, startDate: string, now: Date = new Date()): OrderVendorRow {
  const def = directDefaultDates(startDate, now)
  return { ...row, orderDate: row.orderDate ?? def.orderDate, deliveryDate: row.deliveryDate ?? def.deliveryDate }
}

/**
 * 발송 전 FE 점검(서버와 같은 기준, 서버 검증은 그대로 남음). 사유는 버튼 아래 한 줄·일괄 확인 창에 표시.
 * direct=결과만 직접등록 — [발주 기록] 점검. 필수는 발주일·입고희망일. 품목은 선택, 현장 주소는 칸이 없어 묻지 않는다.
 */
export function checkSendReady(row: OrderVendorRow, draft: OrderDraft, direct = false): { ok: boolean; reasons: string[] } {
  const reasons: string[] = []
  if (!getSendAction(row, direct).single) reasons.push(ORDER_MESSAGE.sendNotAllowed)
  const errs = validateDraft(draft, direct)
  if (!direct && !draft.items.some(i => !isBlankItem(i))) reasons.push(ORDER_MESSAGE.sendNeedItems)
  if (direct && !row.orderDate) reasons.push(ORDER_MESSAGE.sendNeedOrderDate)
  if (!row.deliveryDate) reasons.push(direct ? ORDER_MESSAGE.sendNeedDeliveryDirect : ORDER_MESSAGE.sendNeedDelivery)
  if (!direct && !draft.deliveryAddress.trim()) reasons.push(ORDER_MESSAGE.sendNeedAddress)
  if (hasDraftErrors(errs)) reasons.push(ORDER_MESSAGE.sendNeedFix)
  return { ok: reasons.length === 0, reasons }
}

// ── 작성 상태 배지·하단 집계·패널 머리·단위 선택지 (E-20~E-24) ────────────

/** 서버 상태 DRAFT 행에만 파생하는 작성 단계 — 미작성(입력 없음) / 작성중(일부 입력) / 작성완료(발송 점검 통과) */
export type OrderWriteState = 'EMPTY' | 'WRITING' | 'READY'

export type OrderBadgeStatus = 'mute' | 'warning' | 'success' | 'info'

const WRITE_STATE_BADGE: Record<OrderWriteState, OrderBadgeStatus> = { EMPTY: 'mute', WRITING: 'warning', READY: 'success' }

/**
 * DRAFT 행의 작성 단계. DRAFT 가 아니면 null(발송 이후는 서버 상태 라벨을 그대로 쓴다).
 * 입력 없음 = 품목·양중·현장 주소·입고희망일이 모두 없음. 서버 값을 가정해 채우지 않는다.
 */
export function orderWriteState(row: OrderVendorRow, draft: OrderDraft, direct = false): OrderWriteState | null {
  if (row.orderStatusCode !== 'DRAFT') return null
  if (checkSendReady(row, draft, direct).ok) return 'READY'
  const any = draft.items.some(i => !isBlankItem(i))
    || draft.liftReqYn !== ''
    || draft.deliveryAddress.trim() !== ''
    || !!row.deliveryDate
  return any ? 'WRITING' : 'EMPTY'
}

/** 좌측 목록 거래처 줄 상태 배지 — DRAFT 는 작성 3단계, 발송 이후는 현행 9개 라벨. 모르는 상태는 null(만들어 채우지 않음) */
export function orderStatusBadge(row: OrderVendorRow, draft: OrderDraft, direct = false): { label: string; status: OrderBadgeStatus } | null {
  const ws = orderWriteState(row, draft, direct)
  if (ws) return { label: ORDER_MESSAGE.writeState[ws], status: WRITE_STATE_BADGE[ws] }
  const label = (ORDER_STATUS_LABEL as Record<string, string | undefined>)[row.orderStatusCode]
  return label ? { label, status: 'info' } : null
}

export interface AssignSummary { auto: number; manual: number; unassigned: number }

/**
 * 하단 집계(직접발주 공종 기준) — 자동=AUTO 행, 직접=MANUAL 행, 미배정=활성 거래처 행이 없는 공종.
 * 배정 방식 값이 없는 행(이행 행)은 어느 쪽에도 세지 않는다.
 */
export function summarizeAssign(models: MaterialTaskRowModel[]): AssignSummary {
  const out: AssignSummary = { auto: 0, manual: 0, unassigned: 0 }
  for (const m of models) {
    if (!m.directOrder) continue
    if (m.vendorRows.length === 0) { out.unassigned += 1; continue }
    for (const r of m.vendorRows) {
      if (r.assignMethodCode === ASSIGN_METHOD.AUTO) out.auto += 1
      else if (r.assignMethodCode === ASSIGN_METHOD.MANUAL) out.manual += 1
    }
  }
  return out
}

/** `자동배정 n · 직접배정 n · 미배정 n` */
export function formatAssignSummary(s: AssignSummary): string {
  return `${ORDER_MESSAGE.assignAuto} ${s.auto} · ${ORDER_MESSAGE.assignManual} ${s.manual} · ${ORDER_MESSAGE.unassigned} ${s.unassigned}`
}

/** 현재 공종 다음의 직접발주 공종 id(목록 순서). 없으면 null */
export function nextDirectTaskId(models: MaterialTaskRowModel[], currentTaskId: number): number | null {
  const idx = models.findIndex(m => m.taskId === currentTaskId)
  if (idx < 0) return null
  for (let i = idx + 1; i < models.length; i++) if (models[i].directOrder) return models[i].taskId
  return null
}

/** 작성 패널 머리 한 줄 `착공 M/D · 발주일 M/D · 연락처 … · 자동배정/직접배정` — 값이 없는 조각은 생략 */
export function formatPanelHead(p: {
  startDate: string | null; orderDate: string | null; phone: string | null; assignMethodCode: string | null
}): string {
  const parts: string[] = []
  const s = formatMD(p.startDate)
  if (s) parts.push(ORDER_MESSAGE.popup.headStart(s))
  const o = formatMD(p.orderDate)
  if (o) parts.push(ORDER_MESSAGE.popup.headOrder(o))
  if (p.phone) parts.push(`${ORDER_MESSAGE.popup.contactLabel} ${p.phone}`)
  if (p.assignMethodCode === ASSIGN_METHOD.AUTO) parts.push(ORDER_MESSAGE.assignAuto)
  else if (p.assignMethodCode === ASSIGN_METHOD.MANUAL) parts.push(ORDER_MESSAGE.assignManual)
  return parts.join(' · ')
}

/** 단위 선택지 — 기본 7종. 현재 값이 7종 밖(과거 자유 입력)이면 첫 줄에 추가해 보존 */
export function unitOptions(current: string): string[] {
  const base: string[] = [...ORDER_UNITS]
  return current.trim() !== '' && !base.includes(current) ? [current, ...base] : base
}

// ── 날짜·표기 ─────────────────────────────────────────────────────────

function parseYmdLocal(v: string | null | undefined): Date | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

function parseLocalDateTime(v: string | null | undefined): Date | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(v)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * 리드타임 일수 N = 착공일 − 발주일(달력일). 결정 17: 발주일 = 착공 N일 전(발주 설정값 기준) — 입고일과의 간격이 아니다.
 * 착공일·발주일 중 하나라도 없거나 발주일이 착공일보다 늦으면 null(계산하지 않음)
 */
export function leadTimeDays(orderDate: string | null, startDate: string | null): number | null {
  const a = parseYmdLocal(orderDate)
  const b = parseYmdLocal(startDate)
  if (!a || !b) return null
  const n = Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000)
  return n >= 0 ? n : null
}

/** `리드타임 N일 · 착공 N일 전 발주` (기획 P1-85 문구 틀, N = 착공 − 발주) */
export function leadTimeText(n: number): string {
  return `리드타임 ${n}일 · 착공 ${n}일 전 발주`
}

/**
 * `발주 10/11 → 입고 10/13 · 리드타임 4일 · 착공 4일 전 발주`. 발주일·입고일 중 하나라도 없으면 '발주일·입고일을 정해 주세요'(채우지 않음).
 * 리드타임 조각은 착공일(startDate)이 있고 계산 가능할 때만 붙는다.
 */
export function formatOrderDatesLine(orderDate: string | null, deliveryDate: string | null, startDate?: string | null): string {
  const o = formatMD(orderDate)
  const d = formatMD(deliveryDate)
  if (!o || !d) return ORDER_MESSAGE.datesMissing
  const lead = leadTimeDays(orderDate, startDate ?? null)
  return `발주 ${o} → 입고 ${d}${lead != null ? ` · ${leadTimeText(lead)}` : ''}`
}

/** 발송~회신 경과('N시간 M분'). 둘 중 하나라도 없거나 음수면 null */
export function replyElapsedText(sentDtm: string | null | undefined, replyDtm: string | null | undefined): string | null {
  const a = parseLocalDateTime(sentDtm)
  const b = parseLocalDateTime(replyDtm)
  if (!a || !b) return null
  const min = Math.floor((b.getTime() - a.getTime()) / 60000)
  if (min < 0) return null
  const h = Math.floor(min / 60)
  const m = min % 60
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`
}

function hm(t: string | null | undefined): string | null {
  return t && /^\d{2}:\d{2}/.test(t) ? t.slice(0, 5) : null
}

// ── 거래처 후보 지표 ──────────────────────────────────────────────────

/**
 * 후보·H-5 옵션 둘째 줄(S1): `★ 4.5 · 거래 12회 · 최근 거래 10/9 · 배송지역`. 없는 값은 그 조각만 생략.
 * 거래 0회·별점 없음이면 '아직 거래 이력이 없어요'. 서버가 내려준 % 필드는 값이 있을 때만 추가한다.
 */
export function formatCandidateMetrics(c: Pick<VendorCandidate, 'avgRating' | 'orderCount' | 'lastOrderDate' | 'deliveryRegion'
  | 'replyWithinTwoHoursPercent' | 'onTimePercent' | 'liftAvailPercent'>): string {
  if (c.orderCount === 0 && c.avgRating == null) return ORDER_MESSAGE.noTradeHistory
  const parts: string[] = []
  if (c.avgRating != null) parts.push(`★ ${c.avgRating.toFixed(1)}`)
  parts.push(`거래 ${c.orderCount}회`)
  const last = formatMD(c.lastOrderDate)
  if (last) parts.push(`최근 거래 ${last}`)
  if (c.deliveryRegion) parts.push(c.deliveryRegion)
  if (c.replyWithinTwoHoursPercent != null) parts.push(`발주회신 2시간 내 ${c.replyWithinTwoHoursPercent}%`)
  if (c.onTimePercent != null) parts.push(`납기준수 ${c.onTimePercent}%`)
  if (c.liftAvailPercent != null) parts.push(`양중가능 ${c.liftAvailPercent}%`)
  return parts.join(' · ')
}

// ── 회신 결과 ─────────────────────────────────────────────────────────

export interface ReplySummary {
  result: 'ACCEPTED' | 'DECLINED'
  /** 한 줄 요약 */
  text: string
  /** 불가일 때 가능한 날짜(없으면 null) */
  availDate: string | null
}

/**
 * 회신 한 줄 요약(§3.3 ②·§10.2 ⑦). lastReply 가 없거나 회신 값이 약속된 값 밖이면 null(채우지 않음).
 * 가능 = `가능 · 입고 M/D 확정 · 양중 가능/불가 · 발송 N시간 M분 뒤 회신`(경과는 값이 없으면 생략),
 * 불가 = `불가 · 사유 {라벨} · 가능한 날짜 M/D · {메모}`.
 */
export function describeReply(row: OrderVendorRow): ReplySummary | null {
  const r = row.lastReply
  if (!r) return null
  if (r.replyResultCode === 'ACCEPTED') {
    const parts: string[] = [ORDER_MESSAGE.replyAccepted]
    const md = formatMD(row.deliveryDate)
    if (md) parts.push(`입고 ${md} 확정`)
    if (r.liftAvailYn === 'Y') parts.push(ORDER_MESSAGE.liftOk)
    else if (r.liftAvailYn === 'N') parts.push(ORDER_MESSAGE.liftNg)
    const el = replyElapsedText(row.lastSend?.sentDtm, r.replyDtm)
    if (el) parts.push(`발송 ${el} 뒤 회신`)
    return { result: 'ACCEPTED', text: parts.join(' · '), availDate: null }
  }
  if (r.replyResultCode === 'DECLINED') {
    const parts: string[] = [ORDER_MESSAGE.replyDeclined]
    const label = r.replyReasonCode ? DECLINE_REASON_LABEL[r.replyReasonCode] : undefined
    if (label) parts.push(`사유 ${label}`)
    const avail = formatMD(r.availDate)
    if (avail) parts.push(`가능한 날짜 ${avail}`)
    if (r.replyNote && r.replyNote.trim()) parts.push(r.replyNote.trim())
    return { result: 'DECLINED', text: parts.join(' · '), availDate: r.availDate }
  }
  return null
}

// ── 진행 요약(5단계) ──────────────────────────────────────────────────

/** 세부 내역 팝업 단계 키 — 진행 요약 5단계 순서와 같다 */
export const MATERIAL_STEP_KEYS = ['order-request', 'order-reply', 'ship-request', 'ship-reply', 'receipt'] as const
export type MaterialStepKey = (typeof MATERIAL_STEP_KEYS)[number]
export function isMaterialStepKey(v: string): v is MaterialStepKey {
  return (MATERIAL_STEP_KEYS as readonly string[]).includes(v)
}

/** 한 공종에 지정할 수 있는 자재파트너 수(서버 OrderLedgerService.MAX_VENDORS_PER_TASK 와 같은 값) */
export const MAX_VENDORS_PER_TASK = 5

function currentStepOf(status: string | null): number | null {
  switch (status) {
    case 'RECEIVED': return 5
    case 'SHIP_REPLIED': return 5
    case 'SHIP_WAIT': return 4
    case 'CONFIRMED': return 3
    case 'DECLINED':
    case 'REPLY_WAIT':
    case 'REREPLY_WAIT': return 2
    case 'RESEND_NEEDED':
    case 'DRAFT': return 1
    default: return null
  }
}

/**
 * 자재 진행 요약 5단계(940 R53). 상태·내용은 A1 행(lastSend·lastReply·delivery·receipt)에서만 읽는다.
 * 행이 없으면 1단계 current. 값이 없는 문구는 '-'(채우지 않음). 모르는 상태는 current 없음.
 * ctx.orderDate: 행이 없거나 아직 보내지 않았을 때 '발주일 M/D' 표시용(Q1 공종 값).
 * ctx.direct: 결과만 직접등록(SIF-3) 거래처 — 보내기 전 1단계 문구를 '거래처 시스템에 발주 후 기록'으로.
 */
export function getMaterialProgressSteps(
  row: OrderVendorRow | null, ctx: { orderDate?: string | null; direct?: boolean } = {},
): ConfirmProgressStep[] {
  const T = ORDER_MESSAGE.stepTitles
  const none: string = ORDER_MESSAGE.stepNone
  const status = row ? row.orderStatusCode : null
  const current = row ? currentStepOf(status) : 1

  // 1 발주정보 요청
  let t1: string
  const done1 = !!row?.lastSend
  if (row?.lastSend) {
    const sent = formatMDHM(row.lastSend.sentDtm)
    t1 = sent ? `${sent} 발송${row.lastSend.sendTypeCode === 'ORDER_CHANGE' ? ` · ${ORDER_MESSAGE.stepChangeRequest}` : ''}` : none
  } else if (row) {
    const od = formatMD(ctx.orderDate ?? row.orderDate)
    t1 = `${ctx.direct ? ORDER_MESSAGE.stepRecordInVendorSystem : ORDER_MESSAGE.stepNotSent}${od ? ` · 발주일 ${od}` : ''}`
  } else {
    t1 = ORDER_MESSAGE.stepPickVendor
  }

  // 2 발주정보 회신
  let t2 = none
  const reply = row ? describeReply(row) : null
  const done2 = !!reply
  if (reply) {
    if (reply.result === 'ACCEPTED') {
      const md = formatMD(row!.deliveryDate)
      const lift = row!.lastReply!.liftAvailYn === 'Y' ? ` · ${ORDER_MESSAGE.liftOk}` : row!.lastReply!.liftAvailYn === 'N' ? ` · ${ORDER_MESSAGE.liftNg}` : ''
      t2 = `'${ORDER_MESSAGE.replyAccepted}' 회신${md ? ` · 입고 ${md} 확정` : ''}${lift}`
    } else {
      const r = row!.lastReply!
      const label = r.replyReasonCode ? DECLINE_REASON_LABEL[r.replyReasonCode] : undefined
      const avail = formatMD(r.availDate)
      t2 = `'${ORDER_MESSAGE.replyDeclined}' 회신${label ? ` · 사유 ${label}` : ''}${avail ? ` · 가능한 날짜 ${avail}` : ''}`
    }
  } else if (row?.lastSend) {
    t2 = ORDER_MESSAGE.stepWaitingReply
  }

  // 3 배송정보 요청
  let t3 = none
  const done3 = !!row?.delivery?.sentDtm
  if (row?.delivery?.sentDtm) {
    const sent = formatMDHM(row.delivery.sentDtm)
    t3 = sent ? `${sent} 요청` : none
  } else if (status === 'CONFIRMED') {
    t3 = ORDER_MESSAGE.stepShipBeforeDay
  }

  // 4 배송정보 회신
  let t4 = none
  const done4 = !!row?.delivery?.replyDtm
  if (row?.delivery?.replyDtm) {
    const d = row.delivery
    const arrive = [formatMD(d.arrivalDate), hm(d.arrivalTime)].filter(Boolean).join(' ')
    // 도착 · 기사 이름 · 기사 연락처 · 상차사진 N장(값이 없는 조각은 생략)
    const parts = [
      arrive ? `도착 ${arrive}` : '',
      d.driverName?.trim() ?? '',
      d.driverPhone?.trim() ?? '',
      d.photoCnt != null && d.photoCnt > 0 ? `상차사진 ${d.photoCnt}장` : '',
    ].filter(Boolean)
    t4 = parts.length > 0 ? parts.join(' · ') : none
  } else if (row?.delivery?.sentDtm) {
    t4 = ORDER_MESSAGE.stepWaitingReply
  }

  // 5 입고 내역
  let t5 = none
  const done5 = !!row?.receipt
  if (row?.receipt) {
    const rc = row.receipt
    const md = formatMD(rc.receiptDate)
    const parts = ['입고 완료' + (md ? ` ${md}` : '')]
    if (rc.photoCnt != null) parts.push(`하차사진 ${rc.photoCnt}장`)
    if (rc.qtyCheckedCode === 'ISSUE') parts.push('수량 부족·파손')
    t5 = parts.join(' · ')
  }

  const defs: { text: string; done: boolean }[] = [
    { text: t1, done: done1 }, { text: t2, done: done2 }, { text: t3, done: done3 }, { text: t4, done: done4 }, { text: t5, done: done5 },
  ]
  return defs.map((d, i) => {
    const n = i + 1
    let state: ConfirmProgressStep['state']
    if (current != null && n === current) state = 'current'
    else if ((current == null || n < current) && d.done) state = 'done'
    else state = 'pending'
    // 내역이 있는 단계(done 값이 있는 단계)만 세부 내역 팝업을 연다 — 내역이 없는 단계는 눌러도 열 것이 없다(B-11)
    return { title: T[i], text: d.text, state, ...(d.done && { detailKey: MATERIAL_STEP_KEYS[i] }) }
  })
}

// ── C9 날짜 경고·영향 ─────────────────────────────────────────────────

const WARNING_TEXT: Record<string, string> = {
  ORDER_ALREADY_SENT: ORDER_MESSAGE.warnAlreadySent,
  ORDER_AFTER_DELIVERY: ORDER_MESSAGE.warnOrderAfterDelivery,
  DELIVERY_AFTER_START: ORDER_MESSAGE.warnDeliveryAfterStart,
  STALE_DELIVERY: ORDER_MESSAGE.warnStaleDelivery,
  DELIVERY_RECEIVED: ORDER_MESSAGE.warnDeliveryReceived,
}

/**
 * C9 응답의 경고 코드·영향 행 → 표시 문구(§9.3). 경고는 저장을 막지 않는다(값은 그대로 저장되고 경고만 표시).
 * 모르는 경고 코드는 건너뛴다(문구를 만들어 채우지 않음). 같은 문구는 한 번만.
 */
export function dateWarningMessages(warnings: string[], affected: OrderScheduleAffected[]): string[] {
  const out: string[] = []
  const push = (s: string) => { if (!out.includes(s)) out.push(s) }
  for (const w of warnings) {
    const t = WARNING_TEXT[w]
    if (t) push(t)
  }
  if (affected.some(a => a.toStatusCode === 'RESEND_NEEDED')) push(ORDER_MESSAGE.affectedResend)
  if (affected.some(a => a.shipLinkClosedYn === 'Y')) push(ORDER_MESSAGE.affectedShipClosed)
  return out
}

/** 코드 기본값 상수 노출(시험·호출부가 UNKNOWN 코드 비교에 사용) */
export const ORDER_UNKNOWN_CODE = ORDER_ERROR_UNKNOWN
